import { Podcasts } from '@/podcasts/Podcasts'
import { Library } from '@/library/Library'
import { Find } from '@/find/Find'
import { playSearch } from '@/voice/playSearch'
import { getSearchController, Search } from '@/search/Search'
import type { AppDependencies } from '@/appDependencies'
import { RecentlyAdded } from '@/recentlyAdded/RecentlyAdded'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { AuthStatus } from '@/auth/auth'
import { back, getRouter } from '@/router/router'
import { getDeviceConfig, isTv } from '@/device/deviceConfig'
import { el } from '@amazon/vinyl-tsx'
import { windowEvents } from '@/util/interaction'
import { matchesKey } from '@/device/input/KeyMap'
import { NotFound } from '@/notFound/NotFound'

export function registerRoutes(deps: AppDependencies): Unsubscribe {
    const router = getRouter()

    router.configure({
        stage: el('#stage'),
        routes: [
            {
                pattern: '/',
                paramNames: [],
                factory: async () => {
                    return (await import('@/BrowseHome')).BrowseHome({ deps })
                },
            },
            {
                pattern: '/tastes',
                paramNames: [],
                factory: async () => {
                    return (await import('@/tastes/TastesPage')).TastesPage()
                },
            },
            {
                pattern: '/videos',
                paramNames: [],
                factory: async () => {
                    const { VideosPage } = await import('@/video/VideosPage')
                    return VideosPage({ deps })
                },
            },
            {
                pattern: '/videos/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { VideoDetailPage } = await import('@/video/VideoDetailPage')
                    return VideoDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/profile',
                paramNames: [],
                factory: async () => {
                    return (await import('@/profile/ProfilePage')).ProfilePage()
                },
            },
            {
                pattern: '/profiles',
                paramNames: [],
                factory: async () => {
                    const { ProfilesPage } = await import('@/profile/ProfilesPage')
                    return ProfilesPage()
                },
            },
            {
                pattern: '/subscription',
                paramNames: [],
                factory: async () => {
                    const { SubscriptionPage } = await import('@/subscription/SubscriptionPage')
                    return SubscriptionPage()
                },
            },
            {
                pattern: '/streamingQualitySettings',
                paramNames: [],
                factory: async () => {
                    return (
                        await import('@/streamingQuality/StreamingQualitySettings')
                    ).StreamingQualitySettings()
                },
            },
            {
                pattern: '/help',
                paramNames: [],
                factory: async () => {
                    return (await import('@/help/HelpPage')).HelpPage()
                },
            },
            {
                pattern: '/podcasts',
                paramNames: [],
                factory: () => Podcasts({ deps }),
            },
            {
                // Top-charts grid (green hornet's /podcasts/top-charts).
                // Uses `topPodcastShows` directly rather than a
                // recommender, so it lives as its own page.
                pattern: '/podcasts/top-charts',
                paramNames: [],
                factory: async () => {
                    const { TopPodcastsPage } = await import('@/podcast/TopPodcastsPage')
                    return TopPodcastsPage({ deps })
                },
            },
            // /library kept as a redirect alias so existing
            // bookmarks / links land on the canonical /my/library
            // path. The factory just re-navigates; AppView's router
            // re-renders into the new route entry.
            {
                pattern: '/library',
                paramNames: [],
                factory: async () => {
                    const { navigateTo } = await import('@/router/router')
                    navigateTo('/my/library')
                    return Library({ deps })
                },
            },
            {
                pattern: '/find',
                paramNames: [],
                factory: () => Find({ deps }),
            },
            {
                pattern: '/search',
                paramNames: [],
                factory: () => Search({ controller: getSearchController(), deps }),
            },
            {
                pattern: '/albums/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { AlbumDetailPage } = await import('@/album/AlbumDetailPage')
                    return AlbumDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { TrackDetailPage } = await import('@/track/TrackDetailPage')
                    return TrackDetailPage({ id: params.id, deps })
                },
            },
            {
                // Mirrors GH's TrackRelatedArtistsPage. Both -artists and
                // -playlists use the same `recommendationCollection(type:
                // "track", targets: [trackId])` query — GH's surfaces just
                // pick the rows their wrappers care about. Bullfrog
                // surfaces the full track-scoped recommendation stack;
                // BrowseMusicCollections handles the rendering.
                pattern: '/tracks/:id/related-artists',
                paramNames: ['id'],
                factory: async (params) => {
                    const { BrowseHomePage } = await import('@/browse/BrowseHomePage')
                    return BrowseHomePage({
                        deps,
                        type: 'track',
                        title: 'Related artists',
                        targets: [params.id],
                    })
                },
            },
            {
                pattern: '/tracks/:id/related-playlists',
                paramNames: ['id'],
                factory: async (params) => {
                    const { BrowseHomePage } = await import('@/browse/BrowseHomePage')
                    return BrowseHomePage({
                        deps,
                        type: 'track',
                        title: 'Related playlists',
                        targets: [params.id],
                    })
                },
            },
            {
                pattern: '/artists/:id/related-artists',
                paramNames: ['id'],
                factory: async (params) => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: `uri:///artist/${params.id}/related-artists`,
                        deps,
                    })
                },
            },
            {
                pattern: '/artists/:id/all-access-songs',
                paramNames: ['id'],
                factory: async (params) => {
                    const { ArtistAllAccessSongsPage } =
                        await import('@/artist/ArtistAllAccessSongsPage')
                    return ArtistAllAccessSongsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { ArtistDetailPage } = await import('@/artist/ArtistDetailPage')
                    return ArtistDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/podcasts/:showId',
                paramNames: ['showId'],
                factory: async (params) => {
                    const { PodcastShowDetailPage } =
                        await import('@/podcast/PodcastShowDetailPage')
                    return PodcastShowDetailPage({ id: params.showId, deps })
                },
            },
            {
                // Podcast episode deep link. The trailing slug segment is
                // SEO-only and ignored — only the episode id is used to
                // fetch data. The show id is in the path for breadcrumbs
                // / future "back to show" wiring but not strictly needed.
                pattern: '/podcasts/:showId/episodes/:episodeId/:slug?',
                paramNames: ['showId', 'episodeId', 'slug'],
                factory: async (params) => {
                    const { PodcastEpisodePage } = await import('@/podcast/PodcastEpisodePage')
                    return PodcastEpisodePage({ id: params.episodeId, deps })
                },
            },
            {
                pattern: '/podcasts/:categoryId/:strategyId',
                paramNames: ['categoryId', 'strategyId'],
                factory: async (params) => {
                    const { PodcastSeeMorePage } = await import('@/podcast/PodcastSeeMorePage')
                    return PodcastSeeMorePage({
                        categoryId: params.categoryId,
                        strategyId: params.strategyId,
                        deps,
                    })
                },
            },
            {
                pattern: '/new',
                paramNames: [],
                factory: async () => {
                    const { NewReleasesPage } = await import('@/newReleases/NewReleasesPage')
                    return NewReleasesPage({ deps })
                },
            },
            // /new/* see-all pages — direct-recommender pages green
            // hornet exposes alongside its `/new` index. Each is a
            // flat see-all grid; URIs match
            // `src/pages/New/*/Page.tsx`.
            {
                pattern: '/new/songs',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///new/songs', deps })
                },
            },
            {
                pattern: '/new/albums',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///new/albums', deps })
                },
            },
            {
                pattern: '/new/playlists',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///new/playlists', deps })
                },
            },
            {
                pattern: '/new/new-releases-for-you',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///new/new-releases-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/new/upcoming-releases',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///new/upcoming-releases', deps })
                },
            },
            {
                pattern: '/my/library',
                paramNames: [],
                factory: () => Library({ deps }),
            },
            {
                pattern: '/my/playlists',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await import('@/library/MyPlaylistsPage')
                    return MyPlaylistsPage({ deps, variant: 'all' })
                },
            },
            {
                pattern: '/my/playlists/by-me',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await import('@/library/MyPlaylistsPage')
                    return MyPlaylistsPage({ deps, variant: 'by-me' })
                },
            },
            {
                pattern: '/my/playlists/followed',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await import('@/library/MyPlaylistsPage')
                    return MyPlaylistsPage({ deps, variant: 'followed' })
                },
            },
            {
                pattern: '/my/albums',
                paramNames: [],
                factory: async () => {
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'albums' })
                },
            },
            {
                pattern: '/my/artists',
                paramNames: [],
                factory: async () => {
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'artists' })
                },
            },
            {
                pattern: '/my/songs',
                paramNames: [],
                factory: async () => {
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'songs' })
                },
            },
            {
                pattern: '/my/podcasts/playlist',
                paramNames: [],
                factory: async () => {
                    const { MyPodcastPlaylistPage } =
                        await import('@/library/MyPodcastPlaylistPage')
                    return MyPodcastPlaylistPage({ deps })
                },
            },
            {
                pattern: '/my/podcasts',
                paramNames: [],
                factory: async () => {
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'podcasts' })
                },
            },
            {
                pattern: '/my/purchased',
                paramNames: [],
                factory: async () => {
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'purchased' })
                },
            },
            {
                // Audiobook detail isn't wired in bullfrog yet, so the
                // /my/audiobooks chip lands here on a friendly empty
                // state instead of a 404. Keeps the chip rail clickable
                // without rendering cards that nav to nowhere.
                pattern: '/my/audiobooks',
                paramNames: [],
                factory: async () => {
                    const { MyAudiobooksPage } = await import('@/library/MyAudiobooksPage')
                    return MyAudiobooksPage()
                },
            },
            {
                pattern: '/music',
                paramNames: [],
                factory: async () => {
                    return (await import('@/music/MusicHome')).MusicHome({ deps })
                },
            },
            {
                pattern: '/browse/music-items/home/recommendation/:widgetId',
                paramNames: ['widgetId'],
                factory: async (params) => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ widgetId: params.widgetId, deps })
                },
            },
            // Charts / popular pages — green hornet exposes
            // `/popular/{songs,albums,playlists,trending-songs}` (see
            // `src/app/(app)/.../popular/`). Each is a flat see-all
            // grid backed by a fixed `uri:///popular/...` recommender,
            // so the existing SeeAllPage handles them by passing the
            // full recommenderId rather than a widget id.
            {
                pattern: '/popular/songs',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-songs', deps })
                },
            },
            {
                pattern: '/popular/albums',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-albums', deps })
                },
            },
            {
                pattern: '/popular/playlists',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-playlists', deps })
                },
            },
            {
                pattern: '/popular/trending-songs',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({ recommenderId: 'uri:///popular/trending-songs', deps })
                },
            },
            {
                // Personalized Playlists hub (GH's PlaylistsForYouPage).
                // Same direct-recommender shape as the popular charts.
                pattern: '/my/playlists/personalized',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/playlists-for-you',
                        deps,
                    })
                },
            },
            {
                // Recently-updated playlists (GH's RecentlyUpdated page).
                pattern: '/my/playlists/recently-updated',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/recently-updated',
                        deps,
                    })
                },
            },
            {
                // Hornet exposes a `popular/stations.tsx` chart that maps
                // to the `topStations` schema field. Mirror it as a
                // dedicated page since `topStations` isn't a recommender
                // and so doesn't fit SeeAllPage's `recommender(id:)`
                // wrapper. Also serves as the `/stations` landing
                // until the full StationsPage (StationsForYou +
                // RecentlyPlayed + TopStations shovelers) lands.
                pattern: '/popular/stations',
                paramNames: [],
                factory: async () => {
                    const { TopStationsPage } = await import('@/stations/TopStationsPage')
                    return TopStationsPage({ deps })
                },
            },
            {
                pattern: '/stations',
                paramNames: [],
                factory: async () => {
                    const { TopStationsPage } = await import('@/stations/TopStationsPage')
                    return TopStationsPage({ deps })
                },
            },
            {
                // Popular Artists hub — same direct-recommender shape
                // as /popular/songs etc. Falls back to a clean empty
                // grid if the recommender id isn't available; see-all
                // page handles the empty case.
                pattern: '/artists',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///popular/top-artists',
                        deps,
                    })
                },
            },
            // Playlist see-all pages — same direct-recommender shape
            // as /popular. Source URIs match green hornet's
            // `src/pages/Playlists/PlaylistsForYou` and
            // `RecentlyUpdated` pages.
            {
                pattern: '/playlists/playlists-for-you',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/playlists-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/playlists/recently-updated-playlists',
                paramNames: [],
                factory: async () => {
                    const { SeeAllPage } = await import('@/seeAll/SeeAllPage')
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/recently-updated-playlists',
                        deps,
                    })
                },
            },
            {
                pattern: '/recently/added',
                paramNames: [],
                factory: () => RecentlyAdded({ deps }),
            },
            {
                pattern: '/recently/played/songs',
                paramNames: [],
                factory: async () => {
                    const { SongHistoryPage } = await import('@/library/SongHistoryPage')
                    return SongHistoryPage({ deps })
                },
            },
            {
                pattern: '/recently/played/stations',
                paramNames: [],
                factory: async () => {
                    const { RecentlyPlayedStationsPage } =
                        await import('@/stations/RecentlyPlayedStationsPage')
                    return RecentlyPlayedStationsPage({ deps })
                },
            },
            // Hornet aliases: `/recently/purchased` and
            // `/recently/played` redirect into `/my/...` rather than
            // hosting their own pages. Mirror the redirects so any
            // shared link from hornet lands in a sane place.
            {
                pattern: '/recently/purchased',
                paramNames: [],
                factory: async () => {
                    history.replaceState(null, '', '/my/purchased')
                    const { MyLibraryListPage } = await import('@/library/MyLibraryListPage')
                    return MyLibraryListPage({ deps, kind: 'purchased' })
                },
            },
            {
                pattern: '/recently/played',
                paramNames: [],
                factory: () => {
                    history.replaceState(null, '', '/library')
                    return Library({ deps })
                },
            },
            // Hornet surfaces these top-level routes; Bullfrog doesn't
            // render their entity types yet (Audiobook, LiveEvent,
            // Merch, Activity messages) or wire their mutation flows
            // (Maestro generative playlists). Land URL parity on a
            // shared empty-state stub so deep links don't 404 while
            // the underlying pieces ship in follow-ups.
            {
                pattern: '/maestro',
                paramNames: [],
                factory: async () => {
                    const { MaestroPage } = await import('@/maestro/MaestroPage')
                    return MaestroPage({ deps })
                },
            },
            {
                pattern: '/activity-feed',
                paramNames: [],
                factory: async () => {
                    const { ActivityFeedPage } = await import('@/activity/ActivityFeedPage')
                    return ActivityFeedPage()
                },
            },
            {
                pattern: '/live',
                paramNames: [],
                factory: async () => {
                    const { LiveEventsPage } = await import('@/liveevent/LiveEventsPage')
                    return LiveEventsPage()
                },
            },
            {
                pattern: '/events/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { LiveEventDetailPage } = await import('@/liveevent/LiveEventDetailPage')
                    return LiveEventDetailPage({ id: params.id })
                },
            },
            {
                // Nearby concerts hub — mirrors GH's Concerts page
                // backed by Firefly's nearbyConcerts query (default
                // 100mi radius, upcoming-only).
                pattern: '/concerts',
                paramNames: [],
                factory: async () => {
                    const { ConcertsPage } = await import('@/concert/ConcertsPage')
                    return ConcertsPage()
                },
            },
            {
                pattern: '/concerts/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { ConcertDetailPage } = await import('@/concert/ConcertDetailPage')
                    return ConcertDetailPage({ id: params.id })
                },
            },
            {
                pattern: '/merch',
                paramNames: [],
                factory: async () => {
                    const { MerchShopPage } = await import('@/merch/MerchShopPage')
                    return MerchShopPage({ title: 'Merch' })
                },
            },
            {
                pattern: '/merch/category/:filter',
                paramNames: ['filter'],
                factory: async (params) => {
                    const { MerchShopPage } = await import('@/merch/MerchShopPage')
                    return MerchShopPage({
                        filter: params.filter,
                        title: humanizeMerchFilter(params.filter),
                    })
                },
            },
            {
                pattern: '/merch/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { MerchDetailPage } = await import('@/merch/MerchDetailPage')
                    return MerchDetailPage({ id: params.id })
                },
            },
            {
                pattern: '/genres/:id',
                paramNames: ['id'],
                factory: async (params) => {
                    const { GenreDetailPage } = await import('@/genre/GenreDetailPage')
                    return GenreDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/audiobooks/library',
                paramNames: [],
                factory: async () => {
                    history.replaceState(null, '', '/my/audiobooks')
                    const { MyAudiobooksPage } = await import('@/library/MyAudiobooksPage')
                    return MyAudiobooksPage()
                },
            },
            {
                // /audiobooks is GH's audible browse home — pulls the
                // `audible-editorial-home` recommendation collection
                // (matching Hornet's AudiobooksHomePage) and renders
                // it through the same shoveler stack used by Music
                // Home. Recommendation rows of `Audiobook` typename
                // route to `/audiobooks/:id` via linkTypeMap.
                pattern: '/audiobooks',
                paramNames: [],
                factory: async () => {
                    const { BrowseHomePage } = await import('@/browse/BrowseHomePage')
                    return BrowseHomePage({
                        deps,
                        type: 'audible-editorial-home',
                        title: 'Audible',
                    })
                },
            },
            {
                // Audiobook category browse — matches GH's
                // AudiobookCategoryPage. The categoryId is the
                // recommendation-collection id for the category;
                // the BrowseHomePage type/targets path renders
                // the matching shoveler stack.
                pattern: '/audiobooks/category/:categoryId',
                paramNames: ['categoryId'],
                factory: async (params) => {
                    const { BrowseHomePage } = await import('@/browse/BrowseHomePage')
                    return BrowseHomePage({
                        deps,
                        type: 'audible-category',
                        targets: [params.categoryId],
                        title: 'Audiobook category',
                    })
                },
            },
            {
                // Audiobook detail page. Renders the audiobook header
                // (cover, title, authors, narrators, length, short
                // description). Playback isn't wired yet — that needs
                // the Audible SDK path which Bullfrog doesn't ship,
                // so this is a metadata-only landing for now.
                pattern: '/audiobooks/:audiobookId',
                paramNames: ['audiobookId'],
                factory: async (props) => {
                    const { AudiobookDetailPage } = await import('@/audiobook/AudiobookDetailPage')
                    return AudiobookDetailPage({ id: props.audiobookId })
                },
            },
            {
                pattern: '/now-playing',
                paramNames: [],
                factory: async () => {
                    const { NowPlayingPage } = await import('@/nowPlaying/NowPlayingPage')
                    return NowPlayingPage({ deps })
                },
            },
            {
                // Playlist collaboration invitation. The invite-id route
                // is registered BEFORE the generic /playlists/:id pattern
                // so the invite landing isn't shadowed by the detail page.
                pattern: '/playlists/invites/:inviteId',
                paramNames: ['inviteId'],
                factory: async (params) => {
                    const { PlaylistInvitePage } =
                        await import('@/playlistdetail/PlaylistInvitePage')
                    return PlaylistInvitePage({ inviteId: params.inviteId })
                },
            },
            {
                // A user's public playlists (GH's PublicPlaylistsPage).
                pattern: '/users/:userId/playlists',
                paramNames: ['userId'],
                factory: async (params) => {
                    const { PublicPlaylistsPage } =
                        await import('@/playlistdetail/PublicPlaylistsPage')
                    return PublicPlaylistsPage({ userId: params.userId })
                },
            },
            {
                pattern: '/users/:userId/followers',
                paramNames: ['userId'],
                factory: async (params) => {
                    const { UserFollowsPage } = await import('@/profile/UserFollowsPage')
                    return UserFollowsPage({ userId: params.userId, variant: 'followers' })
                },
            },
            {
                pattern: '/users/:userId/following',
                paramNames: ['userId'],
                factory: async (params) => {
                    const { UserFollowsPage } = await import('@/profile/UserFollowsPage')
                    return UserFollowsPage({ userId: params.userId, variant: 'following' })
                },
            },
            {
                pattern: '/playlists/:playlistId/:mode?',
                paramNames: ['playlistId', 'mode'],
                factory: async (props) => {
                    return (await import('@/playlistdetail/PlaylistDetailPage')).PlaylistDetailPage(
                        {
                            playlistId: props.playlistId,
                            deps,
                            editMode: props.mode === 'edit',
                        }
                    )
                },
            },
            {
                // Alias for shared/user playlist URLs (the one Hornet
                // emits when a user copies a "Listen on Amazon Music"
                // link). Same factory; the playlist id is already
                // namespaced server-side, so no special handling
                // needed.
                pattern: '/user-playlists/:playlistId/:mode?',
                paramNames: ['playlistId', 'mode'],
                factory: async (props) => {
                    return (await import('@/playlistdetail/PlaylistDetailPage')).PlaylistDetailPage(
                        {
                            playlistId: props.playlistId,
                            deps,
                            editMode: props.mode === 'edit',
                        }
                    )
                },
            },
            {
                // Voice / deep-link entry point: bullfrog:///play?q=<query>.
                // Used by the iOS Siri shell to honor "Play X on Bullfrog",
                // and reachable from a browser for testing. The route fires
                // the search-and-play flow, then redirects back to home so
                // the URL bar doesn't sit on a transient action path.
                pattern: '/play',
                paramNames: [],
                factory: async () => {
                    const query = new URL(window.location.href).searchParams.get('q') ?? ''
                    if (query) {
                        playSearch(query, {
                            cloudQueueController: deps.cloudQueueController,
                        }).catch(() => {})
                    }
                    const { BrowseHome } = await import('@/BrowseHome')
                    // Replace the URL so reload doesn't re-trigger playback.
                    history.replaceState(null, '', '/')
                    return BrowseHome({ deps })
                },
            },
        ],
        fallback: NotFound,
    })

    return () => router.clearRoutes()
}

// Maps the merch category filter (used as Firefly's `merch(filters:)`
// argument) to a human-readable page title. Falls back to a startCase
// rendering of the raw filter for unknown ones rather than failing.
function humanizeMerchFilter(filter: string): string {
    const map: Record<string, string> = {
        audioCD: 'CDs',
        lp_record: 'Vinyl',
        cassette: 'Cassettes',
        apparel: 'Apparel',
    }
    return map[filter] ?? filter.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * Reloads the view route when the auth status changes to/from signed in <--> signed out
 */
export function initializeRouter(): Unsubscribe {
    const { add, dispose } = createDisposer()
    const router = getRouter()
    const auth = getAuth()
    router.on('navigating', (event) => {
        // Do not show pages when not logged in on TV.
        if (isTv() && !auth.isSignedIn()) {
            event.preventDefault()
        }

        // Do now allow navigation while signing in; when sign-in completes, a reload will occur.
        if (auth.status.value === AuthStatus.PENDING) {
            event.preventDefault()
        }
    })

    add(
        auth.status.onData((value, previous) => {
            if (previous && value !== AuthStatus.PENDING) {
                router.hardReload()
            }
        })
    )

    // Bind back key
    const { keyMap } = getDeviceConfig()
    add(
        windowEvents.on('keydown', (e) => {
            if (matchesKey(e, keyMap.back)) {
                back()
            }
        })
    )

    return dispose
}
